import React, { useState, useRef, useEffect } from 'react';
import type { PosterData } from '../data/posters';

interface PosterProps {
  poster: PosterData;
  index: number;
  tilt: number;
  shouldAnimate: boolean;
  isModal?: boolean;
  onOpen?: () => void;
}

function pluralizeYears(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 14) return 'лет';
  if (last === 1) return 'год';
  if (last >= 2 && last <= 4) return 'года';
  return 'лет';
}

export default function Poster({
  poster,
  index,
  tilt,
  shouldAnimate,
  isModal,
  onOpen,
}: PosterProps) {
  const { name, age, note, phone, contacts = [], lastSeen, desc, details = [], photo, accent } =
    poster;
  const baseTilt = typeof tilt === 'number' ? tilt : index % 2 === 0 ? -2.5 : 2.5;

  const [mouseSwayAngle, setMouseSwayAngle] = useState(0);
  const posterRef = useRef<HTMLElement>(null);
  const lastMousePos = useRef({ x: 0, y: 0 });

  const animations = ['swayLeft', 'swayRight', 'swayUp', 'swayWave'];
  const animationName = animations[index % animations.length];

  useEffect(() => {
    if (shouldAnimate) {
      setMouseSwayAngle(0);
    }
  }, [shouldAnimate]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isModal || shouldAnimate) return;
    const deltaX = e.clientX - lastMousePos.current.x;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    const swayAmount = Math.max(-5, Math.min(5, deltaX * 0.3));
    setMouseSwayAngle(swayAmount);
  };

  const handleMouseLeave = () => {
    setMouseSwayAngle(0);
  };

  const sz = isModal
    ? {
        padding: 28,
        h1: 38,
        h2: 28,
        photoW: 'min(280px, 38vw)',
        photoH: 'min(360px, 48vw)',
        gap: 24,
        gapBottom: 22,
        textBase: 18,
        textSmall: 16,
        leadFont: 17,
        contactsFont: 22,
        tapeTop: -50,
        tapeWidth: 96,
      }
    : {
        padding: 12,
        h1: 20,
        h2: 16,
        photoW: 140,
        photoH: 180,
        gap: 12,
        gapBottom: 12,
        textBase: 11,
        textSmall: 10,
        leadFont: 11,
        contactsFont: 13,
        tapeTop: -33,
        tapeWidth: 60,
      };

  return (
    <article
      ref={posterRef}
      className={`poster poster--alert ${isModal ? 'poster--modal' : ''}`}
      style={
        {
          '--tilt': `${baseTilt}deg`,
          '--accent': accent,
          animation: shouldAnimate ? `${animationName} 4s ease-in-out 1` : 'none',
          transform: shouldAnimate
            ? undefined
            : `rotate(calc(var(--tilt) + ${mouseSwayAngle}deg))`,
          transition: shouldAnimate ? undefined : 'transform 0.2s ease-out',
          position: 'relative',
          backgroundImage: 'url(/missing-wall/poster-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          border: 'none',
        } as React.CSSProperties
      }
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {!isModal && (
        <button
          type="button"
          className="poster__open-btn"
          aria-label="Открыть постер"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof onOpen === 'function') onOpen();
          }}
        >
          ⤢
        </button>
      )}
      <img
        src="/missing-wall/tape.png"
        alt=""
        className="poster__tape"
        style={{
          position: 'absolute',
          top: sz.tapeTop,
          left: '53%',
          transform: 'translateX(-50%)',
          width: sz.tapeWidth,
          height: 'auto',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
      <div className="poster__content poster__content--alert">
        <div
          style={{
            fontFamily: 'Arial, sans-serif',
            width: '100%',
            height: '100%',
            padding: sz.padding,
            backgroundColor: 'transparent',
            color: '#000',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h1
            style={{
              fontSize: sz.h1,
              fontWeight: 800,
              margin: '0 0 4px',
              color: '#000',
              textAlign: 'center',
              textShadow:
                '1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)',
            }}
          >
            {note || 'ВНИМАНИЕ! ПРОПАЛ РЕБЕНОК!'}
          </h1>
          <h2
            style={{
              fontSize: sz.h2,
              fontWeight: 700,
              margin: `0 0 ${sz.gapBottom + 4}px`,
              color: '#000',
              textAlign: 'center',
              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
            }}
          >
            {name}
            {age > 0 ? `, ${age} ${pluralizeYears(age)}.` : ''}
          </h2>

          <div style={{ display: 'flex', gap: sz.gap, marginBottom: sz.gapBottom }}>
            <div
              style={{
                flexShrink: 0,
                width: sz.photoW,
                height: sz.photoH,
                backgroundColor: '#f0f0f0',
                border: '2px solid #000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {photo ? (
                <img
                  src={photo}
                  alt={`Фото: ${name}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'grayscale(1)',
                  }}
                />
              ) : (
                <span style={{ fontSize: sz.textBase }}>Фото</span>
              )}
            </div>

            <div
              style={{
                flex: 1,
                textAlign: 'left',
                backgroundColor: 'transparent',
                color: '#000',
                fontSize: sz.textBase,
                lineHeight: 1.4,
                textShadow: '1px 1px 2px rgba(255,255,255,0.9)',
              }}
            >
              <p style={{ margin: '0 0 4px' }}>
                <strong>
                  {name}
                  {age > 0 ? `, ${age} ${pluralizeYears(age)}.` : ''}
                </strong>
              </p>
              {lastSeen && (
                <p style={{ margin: '0 0 4px', fontSize: sz.textSmall }}>{lastSeen}</p>
              )}
              {desc && (
                <p style={{ margin: '0 0 6px', fontSize: sz.textBase, lineHeight: 1.35 }}>
                  {desc}
                </p>
              )}

              {details.length > 0 && (
                <>
                  <p style={{ margin: '6px 0 3px', fontWeight: 700 }}>
                    <strong>Приметы:</strong>
                  </p>
                  {details.map((item) => (
                    <p key={item} style={{ margin: '1px 0', fontSize: sz.textSmall }}>
                      {item}
                    </p>
                  ))}
                </>
              )}

              {contacts && contacts.length === 0 && phone && (
                <p style={{ margin: '10px 0 4px' }}>Для связи: {phone}</p>
              )}
            </div>
          </div>

          <p
            style={{
              margin: '8px 0 6px',
              fontSize: sz.leadFont,
              color: '#000',
              textAlign: 'center',
              fontWeight: 600,
              textShadow: '1px 1px 2px rgba(255,255,255,0.9)',
            }}
          >
            Всем, кто может сообщить информацию о местонахождении {name}, просьба звонить:
          </p>

          {contacts && contacts.length > 0 && (
            <div
              style={{
                margin: 0,
                fontSize: sz.contactsFont,
                fontWeight: 700,
                color: '#000',
                textAlign: 'center',
                textShadow: '1px 1px 2px rgba(255,255,255,0.9)',
              }}
            >
              {contacts.map((line) => (
                <p key={line} style={{ margin: '2px 0' }}>
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
